import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Field } from 'src/app/models_new/classes/field';
import { FieldType } from 'src/app/models_new/types/field-type';
import { BackofficeService } from 'src/app/services/api/backoffice.service';
import { Observable, combineLatest, map, startWith, take, tap } from 'rxjs';
import { IHwSwType } from '../../../models_new/types/robot-config/hw-sw-type';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { IApiOrganization } from 'src/app/models_new/classes/api-models/ApiOrganization';
import { ObjectUtils } from 'src/app/utils/object';

@Component({
  selector: 'app-edit-hardware',
  templateUrl: './edit-hardware.component.html',
  styleUrls: ['./edit-hardware.component.scss'],
})
export class EditHardwareComponent implements OnInit {
  hardware: IHwSwType;

  metadataFields: Field[];
  advancedFields: Field[] = [];

  // Ownership field variables.
  public orgSearchFormCtrl = new FormControl<string>(null);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  orgSearched$: Observable<IApiOrganization[]>;
  selectedOrgs: IApiOrganization[] = [];

  currency = 'EUR';
  currencyMap = {
    Euro: 'EUR',
    Dollar: 'USD',
    Pound: 'GBP',
    Nok: 'NOK',
  };

  constructor(
    public dialogRef: MatDialogRef<EditHardwareComponent>,
    @Inject(MAT_DIALOG_DATA)
    public input: {
      hardware: IHwSwType;
      isBackoffice?: boolean;
    },
    private backOfficeApi: BackofficeService
  ) {}

  ngOnInit(): void {
    this.hardware = this.input.hardware;

    const allorgs$ = this.backOfficeApi.getAllOrganizations().pipe(
      take(1),
      tap((orgs) => {
        const preSelectedOrgs = orgs.filter((org) =>
          this.input.hardware.organization_hw_types.some(
            (currentOrg) => currentOrg.organization_id === org.id
          )
        );
        this.selectedOrgs = preSelectedOrgs;
      })
    );

    this.orgSearched$ = combineLatest([
      allorgs$,
      this.orgSearchFormCtrl.valueChanges.pipe(startWith(null)),
    ]).pipe(
      map(([allOrgs, orgSearch]) => {
        const organizationList = Object.values(allOrgs);
        return orgSearch && typeof orgSearch === 'string'
          ? this._filter(orgSearch, organizationList)
          : organizationList.slice();
      })
    );

    this.metadataFields = [
      this.createField('Label', this.hardware.label, 'label', true),
      this.createField(
        'Description',
        this.optionalGetter(this.hardware.metadata.description, ''),
        'metadata.description'
      ),
      new Field(
        FieldType.SELECT_SINGLE,
        false,
        this.hardware.metadata.currency
          ? this.hardware.metadata.currency
          : 'Euro',
        [],
        'currency',
        ['Euro', 'Dollar', 'Pound', 'Nok'],
        null,
        { label: 'Currency', name: 'currency' },
        'metadata.currency'
      ),
      this.createField(
        'Price',
        this.optionalGetter(this.hardware.metadata.price, ''),
        'metadata.price',
        false,
        FieldType.NUMBER,
        'currency'
      ),
      this.createField(
        'Discount',
        this.optionalGetter(this.hardware.metadata.discount, ''),
        'metadata.discount',
        false,
        FieldType.NUMBER,
        'currency',
        '%'
      ),
      new Field(
        FieldType.SLIDETOGGLE,
        false,
        this.hardware.public,
        [],
        'public-toggle',
        null,
        null,
        { label: 'Publicly available' },
        'public'
      ),
    ];
    const metadata = this.optionalGetter(this.hardware.metadata, {});
    if (metadata.currency) {
      this.currency = this.currencyMap[metadata.currency];
    }
    if (this.hardware.metadata['pallet_offset_x']) {
      this.advancedFields.push(
        this.createField(
          'Pallet Orizontal Offset',
          this.hardware.metadata['pallet_offset_x'],
          'metadata.pallet_offset_x',
          false,
          FieldType.NUMBER,
          'mm'
        )
      );
      this.advancedFields.push(
        this.createField(
          'Pallet Vertical Offset',
          this.hardware.metadata['pallet_offset_z'] || 0,
          'metadata.pallet_offset_z',
          false,
          FieldType.NUMBER,
          'mm'
        )
      );
    }
  }

  public saveAndClose() {
    const delta = {};
    let objRef = delta;
    /**
     * @todo: Temporary workaround until the chip-list is implemented as Field.
     * Learn more {@link https://rocketfarm.atlassian.net/browse/PALLY-4042 | here}
     */
    const orgField = new Field(
      FieldType.SELECT_MULTIPLE_OBJECT,
      false,
      ObjectUtils.cloneObject(this.selectedOrgs),
      [],
      'org',
      null,
      null,
      { label: 'Organizations' },
      'organization_hw_types_changes'
    );
    const allFields: Field[] = [
      ...this.metadataFields,
      ...this.advancedFields,
      orgField,
    ];
    for (const field of allFields) {
      const keys = field.id.split('.');
      const val = field.getValue();
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (i === keys.length - 1) {
          objRef[key] = val;
        } else {
          if (!delta[key]) {
            delta[key] = {};
          }
          objRef = delta[key];
        }
      }
      objRef = delta;
    }
    this.dialogRef.close(delta);
  }

  private createField(
    label: string,
    defaultVal: string,
    id?: string,
    required: boolean = false,
    type: FieldType = FieldType.TEXT,
    groupId?: string,
    suffix?: string
  ): Field {
    return new Field(
      type,
      required,
      defaultVal,
      [],
      groupId,
      null,
      null,
      { label: label, name: label.toLowerCase(), suffix: suffix },
      id ? id : label.toLowerCase()
    );
  }

  private optionalGetter(value: any, fallback: any): any {
    if (value) return value;
    else return fallback;
  }

  /**
   * Remove an organization from the selected organizations list.
   * @param {IApiOrganization} org The organization to remove.
   */
  public removeOrg(org: IApiOrganization): void {
    const index = this.selectedOrgs.indexOf(org);
    if (index >= 0) {
      this.selectedOrgs.splice(index, 1);
    }
  }

  /**
   * Adds an organization to the selected organizations list.
   * @param {any}event The event containing the selected organization.
   */
  public setSelectedOrg(event: any): void {
    const value: IApiOrganization = event.option.value;
    if (value) {
      this.selectedOrgs.push(value);
    }
  }

  /**
   * Filters the organizations based on the search value.
   * @param {string} value The search value.
   * @param {IApiOrganization[]} options The list of organizations to filter.
   * @returns {IApiOrganization[]} The filtered list of organizations.
   */
  private _filter(
    value: string,
    options: IApiOrganization[]
  ): IApiOrganization[] {
    const filterValue = value.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }
}
